import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileBottomTabBar } from "../MobileAppShell";
import { withMobileView } from "../MobileAppShell/mobileNavigation";
import {
  useMystudioNotificationPreferencesV4,
  useMystudioTermsV4,
  useUpdateNotificationPreferencesV4,
} from "../../Common/api/v4";
import MystudioDataState from "../MystudioDataState";
import "./MyPageSettingsPage.css";

const APP_VERSION = process.env.REACT_APP_VERSION || "1.0.0";

const NOTIFICATION_ITEMS = [
  {
    key: "scheduleNotifications",
    title: "촬영 일정 알림",
    description: "예약 확정과 다가오는 촬영 일정을 알려드려요.",
    icon: "calendar",
  },
  {
    key: "photoNotifications",
    title: "사진 안내 알림",
    description: "원본 업로드와 다운로드 마감일을 놓치지 않게 알려드려요.",
    icon: "photo",
  },
  {
    key: "reviewEventNotifications",
    title: "후기 이벤트 알림",
    description: "이벤트 신청 결과와 보정 사진 업로드 소식을 알려드려요.",
    icon: "bell",
  },
  {
    key: "productNotifications",
    title: "제품 제작 알림",
    description: "사진 선택 요청과 제작·출고 진행 상태를 알려드려요.",
    icon: "package",
  },
];

const TERMS_DOCUMENTS = [
  {
    id: "service",
    title: "서비스 이용약관",
    summary: "회원 가입, 예약, 촬영·제품 서비스 이용 기준",
    effectiveDate: "2026.07.01",
    sections: [
      {
        title: "1. 목적과 적용 범위",
        body: "본 약관은 고객이 스튜디오 고객 앱에서 예약, 촬영 일정, 사진, 제품 제작 서비스를 이용할 때 필요한 기본 사항을 안내합니다.",
      },
      {
        title: "2. 회원 계정",
        body: "고객은 본인의 계정 정보를 정확하게 관리해야 하며, 연락처가 변경된 경우 마이페이지에서 최신 정보로 수정해야 합니다.",
      },
      {
        title: "3. 예약·촬영·제품 제작",
        body: "예약 변경과 취소, 촬영 진행, 제품 구성 및 제작 일정은 고객이 스튜디오와 체결한 계약 내용과 개별 안내를 우선 적용합니다.",
      },
      {
        title: "4. 사진 보관",
        body: "원본 사진은 화면에 표시된 기간 동안 다운로드할 수 있습니다. 기간 종료 후에는 서비스 운영 정책에 따라 웹용 사진만 보관될 수 있습니다.",
      },
      {
        title: "5. 문의",
        body: "서비스 이용 중 궁금한 점은 마이페이지의 1:1 문의를 통해 스튜디오에 확인할 수 있습니다.",
      },
    ],
  },
  {
    id: "privacy",
    title: "개인정보 처리방침",
    summary: "개인정보의 수집, 이용, 보관 및 고객 권리 안내",
    effectiveDate: "2026.07.01",
    sections: [
      {
        title: "1. 수집하는 정보",
        body: "서비스 제공을 위해 이름, 연락처, 계정 정보, 예약·계약 정보, 촬영 및 제품 제작에 필요한 사진 정보를 처리할 수 있습니다.",
      },
      {
        title: "2. 이용 목적",
        body: "수집한 정보는 본인 확인, 예약과 촬영 진행, 사진 전달, 제품 제작·배송, 고객 문의 처리와 중요 알림 제공에 이용합니다.",
      },
      {
        title: "3. 보관과 파기",
        body: "개인정보는 관련 법령과 계약에서 정한 기간 동안 보관한 뒤 안전하게 파기합니다. 원본 사진의 다운로드 가능 기간은 촬영별 사진 화면에서 확인할 수 있습니다.",
      },
      {
        title: "4. 고객의 권리",
        body: "고객은 본인의 개인정보 열람, 정정, 삭제 및 처리 정지를 요청할 수 있습니다. 요청은 1:1 문의를 통해 접수할 수 있습니다.",
      },
    ],
  },
  {
    id: "marketing",
    title: "마케팅 정보 수신 동의",
    summary: "이벤트, 혜택 및 새로운 서비스 소식 수신 안내",
    effectiveDate: "2026.07.01",
    sections: [
      {
        title: "1. 안내 내용",
        body: "스튜디오 이벤트, 촬영 상품, 할인 혜택과 새로운 서비스 소식을 앱 알림 등으로 안내할 수 있습니다.",
      },
      {
        title: "2. 선택 동의",
        body: "마케팅 정보 수신은 선택 사항이며, 동의하지 않아도 예약·촬영·사진·제품 제작 등 기본 서비스를 이용할 수 있습니다.",
      },
      {
        title: "3. 동의 철회",
        body: "마이페이지의 마케팅 수신 스위치 또는 앱 설정 화면에서 언제든지 수신 동의를 변경할 수 있습니다.",
      },
    ],
  },
];

function BackHeader({ title, onBack }) {
  return (
    <header className="mypage-settings-header">
      <button type="button" onClick={onBack} aria-label="마이페이지로 돌아가기">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m15 5-7 7 7 7" />
        </svg>
      </button>
      <h1>{title}</h1>
      <span aria-hidden="true" />
    </header>
  );
}

function SettingsIcon({ name }) {
  if (name === "calendar") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6.5 5.75h11A1.75 1.75 0 0 1 19.25 7.5v10a1.75 1.75 0 0 1-1.75 1.75h-11a1.75 1.75 0 0 1-1.75-1.75v-10A1.75 1.75 0 0 1 6.5 5.75Z" />
        <path d="M8.25 4.25v3M15.75 4.25v3M4.75 9.25h14.5" />
      </svg>
    );
  }

  if (name === "photo") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5.25 6.25h13.5v11.5H5.25z" />
        <path d="m7.25 15 3.25-3.25 2.25 2.25 1.5-1.5 2.5 2.5M15.5 9.5h.01" />
      </svg>
    );
  }

  if (name === "package") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="m5.25 8 6.75-3.5L18.75 8v8L12 19.5 5.25 16V8Z" />
        <path d="M5.5 8 12 11.5 18.5 8M12 11.5v8M9 6l6.5 3.5" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.25 16.25h9.5l-1-1.45V11a3.75 3.75 0 1 0-7.5 0v3.8l-1 1.45Z" />
      <path d="M10.5 18.25h3" />
    </svg>
  );
}

function Switch({ checked, disabled = false, label, onChange }) {
  return (
    <button
      className={checked ? "mypage-settings-switch is-on" : "mypage-settings-switch"}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span aria-hidden="true" />
    </button>
  );
}

function SettingRow({ icon, title, description, checked, disabled, onChange }) {
  return (
    <div className={disabled ? "mypage-settings-row is-disabled" : "mypage-settings-row"}>
      <span className="mypage-settings-row__icon">
        <SettingsIcon name={icon} />
      </span>
      <span className="mypage-settings-row__copy">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <Switch checked={checked} disabled={disabled} label={title} onChange={onChange} />
    </div>
  );
}

function MyPageSubpage({ title, children }) {
  const navigate = useNavigate();
  const handleNavigate = (target) => navigate(withMobileView(target));

  return (
    <section className="mypage-settings-page mobile-app-frame">
      <BackHeader title={title} onBack={() => handleNavigate("/tabs/mypage")} />
      {children}
      <MobileBottomTabBar activePath="/tabs/mypage" onNavigate={handleNavigate} />
    </section>
  );
}

const EMPTY_NOTIFICATION_SETTINGS = {
  notificationsEnabled: false,
  scheduleNotifications: false,
  photoNotifications: false,
  productNotifications: false,
  reviewEventNotifications: false,
  marketingConsent: false,
};

function mapNotificationPreferences(preferences) {
  if (!preferences) return EMPTY_NOTIFICATION_SETTINGS;
  return {
    notificationsEnabled: Boolean(
      preferences.scheduleEnabled
      || preferences.reviewEventEnabled
      || preferences.productEnabled
      || preferences.serviceEnabled,
    ),
    scheduleNotifications: Boolean(preferences.scheduleEnabled),
    photoNotifications: Boolean(preferences.serviceEnabled),
    productNotifications: Boolean(preferences.productEnabled),
    reviewEventNotifications: Boolean(preferences.reviewEventEnabled),
    marketingConsent: Boolean(preferences.marketingEnabled),
  };
}

function toNotificationPreferences(settings, currentPreferences) {
  return {
    marketingEnabled: Boolean(settings.marketingConsent),
    scheduleEnabled: Boolean(settings.scheduleNotifications),
    reviewEventEnabled: Boolean(settings.reviewEventNotifications),
    productEnabled: Boolean(settings.productNotifications),
    serviceEnabled: Boolean(settings.photoNotifications),
    quietHoursStart: currentPreferences?.quietHoursStart || null,
    quietHoursEnd: currentPreferences?.quietHoursEnd || null,
  };
}

export function AppSettingsPage() {
  const preferencesQuery = useMystudioNotificationPreferencesV4();
  const updatePreferencesMutation = useUpdateNotificationPreferencesV4();
  const [settings, setSettings] = useState(EMPTY_NOTIFICATION_SETTINGS);
  const [savedMessage, setSavedMessage] = useState("");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const preferences = preferencesQuery.data?.data;

  useEffect(() => {
    if (preferences?.availability !== "available") return;
    setSettings(mapNotificationPreferences(preferences));
  }, [preferences]);

  useEffect(() => {
    if (!savedMessage) return undefined;
    const timeoutId = window.setTimeout(() => setSavedMessage(""), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [savedMessage]);

  const updateSetting = async (key, value) => {
    if (preferences?.availability !== "available" || updatePreferencesMutation.isPending) return;

    const nextSettings = key === "notificationsEnabled"
      ? {
          ...settings,
          notificationsEnabled: value,
          scheduleNotifications: value,
          photoNotifications: value,
          productNotifications: value,
          reviewEventNotifications: value,
        }
      : {
          ...settings,
          [key]: value,
        };
    nextSettings.notificationsEnabled = Boolean(
      nextSettings.scheduleNotifications
      || nextSettings.photoNotifications
      || nextSettings.productNotifications
      || nextSettings.reviewEventNotifications,
    );

    setSaveErrorMessage("");
    try {
      const response = await updatePreferencesMutation.mutateAsync(
        toNotificationPreferences(nextSettings, preferences),
      );
      if (response.data?.availability !== "available") {
        throw new Error(response.data?.reasonCode || "알림 설정 저장 기능을 사용할 수 없습니다.");
      }
      setSettings(mapNotificationPreferences(response.data));
      setSavedMessage("설정을 저장했어요.");
    } catch (error) {
      setSaveErrorMessage(error?.message || "알림 설정을 저장하지 못했습니다.");
    }
  };

  const notificationSummary = useMemo(() => {
    if (!settings.notificationsEnabled) return "모든 앱 알림이 꺼져 있어요.";
    const enabledCount = NOTIFICATION_ITEMS.filter((item) => settings[item.key]).length;
    return `중요 알림 ${enabledCount}개를 받고 있어요.`;
  }, [settings]);

  const renderSettingsState = () => {
    if (!preferencesQuery.configured) {
      return (
        <MystudioDataState
          type="error"
          title="알림 설정 API가 연결되지 않았어요"
          description="MyStudio API 연결 주소를 확인해 주세요."
        />
      );
    }
    if (preferencesQuery.isLoading) {
      return <MystudioDataState type="loading" title="알림 설정을 불러오고 있어요" />;
    }
    if (preferencesQuery.error) {
      return (
        <MystudioDataState
          type="error"
          error={preferencesQuery.error}
          onRetry={preferencesQuery.refetch}
        />
      );
    }
    if (preferences?.availability !== "available") {
      return (
        <MystudioDataState
          type="empty"
          title="알림 설정 기능을 준비하고 있어요"
          description={preferences?.reasonCode || "ERP 알림 설정 스키마가 연결되면 이용할 수 있어요."}
        />
      );
    }
    return null;
  };

  const settingsState = renderSettingsState();
  const switchDisabled = Boolean(settingsState) || updatePreferencesMutation.isPending;

  return (
    <MyPageSubpage title="앱 설정">
      <main className="mypage-settings-scroll">
        {settingsState || (
          <>
            <section className="mypage-settings-hero">
              <span className="mypage-settings-hero__icon"><SettingsIcon name="bell" /></span>
              <div>
                <span>알림 설정</span>
                <h2>필요한 소식만 받아보세요</h2>
                <p>{notificationSummary}</p>
              </div>
            </section>

            <section className="mypage-settings-panel">
              <div className="mypage-settings-panel__heading">
                <div>
                  <h2>앱 알림</h2>
                  <p>촬영과 제작에 필요한 알림을 한 번에 켜고 끌 수 있어요.</p>
                </div>
                <Switch
                  checked={settings.notificationsEnabled}
                  disabled={switchDisabled}
                  label="앱 알림"
                  onChange={(value) => updateSetting("notificationsEnabled", value)}
                />
              </div>
              <div className="mypage-settings-list">
                {NOTIFICATION_ITEMS.map((item) => (
                  <SettingRow
                    key={item.key}
                    icon={item.icon}
                    title={item.title}
                    description={item.description}
                    checked={settings[item.key]}
                    disabled={switchDisabled || !settings.notificationsEnabled}
                    onChange={(value) => updateSetting(item.key, value)}
                  />
                ))}
              </div>
            </section>

            <section className="mypage-settings-panel">
              <div className="mypage-settings-panel__title">
                <h2>선택 안내</h2>
              </div>
              <SettingRow
                icon="bell"
                title="마케팅 정보 수신"
                description="이벤트와 스튜디오 혜택 소식을 받아보세요."
                checked={settings.marketingConsent}
                disabled={switchDisabled}
                onChange={(value) => updateSetting("marketingConsent", value)}
              />
            </section>

            {saveErrorMessage ? (
              <p className="mypage-settings-save-error" role="alert">{saveErrorMessage}</p>
            ) : null}
          </>
        )}

        <section className="mypage-settings-panel mypage-settings-app-info">
          <div>
            <span>현재 버전</span>
            <strong>{APP_VERSION}</strong>
          </div>
          <p>알림 설정은 계정에 저장되어 다른 기기에서도 동일하게 적용됩니다.</p>
        </section>
      </main>
      <div className={savedMessage ? "mypage-settings-toast is-visible" : "mypage-settings-toast"} role="status" aria-live="polite">
        {savedMessage}
      </div>
    </MyPageSubpage>
  );
}

function TermsAccordion({ document, expanded, onToggle }) {
  const panelId = `terms-panel-${document.id}`;

  return (
    <article className={expanded ? "mypage-terms-card is-open" : "mypage-terms-card"}>
      <button
        className="mypage-terms-card__button"
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span>
          <strong>{document.title}</strong>
          <small>{document.summary}</small>
        </span>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 10 4 4 4-4" /></svg>
      </button>
      {expanded ? (
        <div className="mypage-terms-card__content" id={panelId}>
          <p className="mypage-terms-card__date">시행일 {document.effectiveDate}</p>
          {document.sections.map((section) => (
            <section key={section.title}>
              <h3>{section.title}</h3>
              <p>{section.body}</p>
            </section>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export function TermsPage() {
  const [expandedDocumentId, setExpandedDocumentId] = useState("service");
  const termsQuery = useMystudioTermsV4();
  const termsDocuments = useMemo(() => {
    const typeIds = {
      SERVICE: "service",
      PRIVACY: "privacy",
      MARKETING: "marketing",
    };
    return (termsQuery.data?.data || []).map((terms) => {
      const id = typeIds[terms.type] || String(terms.type || terms.id).toLowerCase();
      const legacyMeta = TERMS_DOCUMENTS.find((document) => document.id === id);
      return {
        id,
        title: terms.title,
        summary: legacyMeta?.summary || `${terms.version} 버전`,
        effectiveDate: terms.effectiveFrom
          ? String(terms.effectiveFrom).slice(0, 10).replace(/-/g, ".")
          : "-",
        sections: [{
          title: `${terms.title} · ${terms.version}`,
          body: terms.content,
        }],
      };
    });
  }, [termsQuery.data]);

  useEffect(() => {
    if (
      termsDocuments.length > 0
      && !termsDocuments.some((document) => document.id === expandedDocumentId)
    ) {
      setExpandedDocumentId(termsDocuments[0].id);
    }
  }, [expandedDocumentId, termsDocuments]);

  return (
    <MyPageSubpage title="이용약관">
      <main className="mypage-settings-scroll mypage-terms-scroll">
        <section className="mypage-terms-intro">
          <span>MY STUDIO</span>
          <h2>안심하고 이용하실 수 있도록<br />꼭 필요한 내용을 안내해요.</h2>
          <p>문서를 눌러 최신 적용 내용을 확인해 주세요.</p>
        </section>

        <section className="mypage-terms-list" aria-label="약관 문서">
          {!termsQuery.configured ? (
            <MystudioDataState type="error" title="약관 API가 연결되지 않았어요" />
          ) : termsQuery.isLoading ? (
            <MystudioDataState type="loading" title="ERP 약관을 불러오고 있어요" />
          ) : termsQuery.error ? (
            <MystudioDataState
              type="error"
              error={termsQuery.error}
              onRetry={() => termsQuery.refetch()}
            />
          ) : termsDocuments.length === 0 ? (
            <MystudioDataState
              type="empty"
              title="게시된 약관이 없어요"
              description="ERP 고객 동의 약관 관리에서 약관을 게시해주세요."
            />
          ) : termsDocuments.map((document) => (
            <TermsAccordion
              key={document.id}
              document={document}
              expanded={expandedDocumentId === document.id}
              onToggle={() => setExpandedDocumentId((currentId) => currentId === document.id ? "" : document.id)}
            />
          ))}
        </section>

        <p className="mypage-terms-notice">
          예약 변경·취소와 상품 구성 등 고객별 조건은 스튜디오와 체결한 계약서 및 개별 안내가 우선합니다.
        </p>
      </main>
    </MyPageSubpage>
  );
}
